import React from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { Gift, Check, Copy, Users, ChartBar, DollarSign, Crown, RefreshCw } from 'lucide-react';

// Add any other imports needed for the referral logic

interface ReferralModalProps {
  isOpen: boolean;
  onClose: () => void;
  referralCode: string;
  referralLink: string;
  linkCopied: boolean;
  isLoadingReferral: boolean;
  referralStats: {
    totalReferrals: number;
    activeSignups: number;
    subscribed: number;
    minutesEarned: number;
  };
  ambassadorStatus: any;
  ambassadorEarnings: any;
  ambassadorPayouts: any;
  showReferralStatsModal: boolean;
  showAmbassadorPaymentsModal: boolean;
  referredUsers: any[];
  onCopyReferralLink: () => void;
  onOpenReferralStats: () => void;
  onOpenAmbassadorPayments: () => void;
  onRefresh: () => void;
  onCloseReferralStats: () => void;
  onCloseAmbassadorPayments: () => void;
  formatDate?: (dateString: string) => string;
  isMobileView: boolean;
  zIndex?: number;
}

const ReferralModal: React.FC<ReferralModalProps> = ({
  isOpen,
  onClose,
  referralCode,
  referralLink,
  linkCopied,
  isLoadingReferral,
  referralStats,
  ambassadorStatus,
  ambassadorEarnings,
  ambassadorPayouts,
  showReferralStatsModal,
  showAmbassadorPaymentsModal,
  referredUsers,
  onCopyReferralLink,
  onOpenReferralStats,
  onOpenAmbassadorPayments,
  onRefresh,
  onCloseReferralStats,
  onCloseAmbassadorPayments,
  formatDate,
  isMobileView,
  zIndex
}) => {
  if (!isOpen) return null;

  // Default formatDate if not provided
  const formatDateFn = formatDate || ((dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  });

  return (
    <Modal 
      isOpen={isOpen}
      onClose={onClose}
      title="Refer & Earn" 
      maxWidth="600px" 
      preventOutsideClick={true} 
      zIndex={zIndex} >
      <div className="p-6 space-y-6 animate-fade-in">
        <div>
            <div className="w-fit flex flex-col gap-3">
                {ambassadorStatus.isAmbassador && ambassadorStatus.status === 'approved' && (
                    <div className="w-fit flex items-center gap-2 px-3 py-1 -mt-3 md:mt-0 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border border-amber-500/20 rounded-full">
                        <Crown size={15} className="text-amber-600 dark:text-amber-400" />
                        <span className="text-xs md:text-sm font-medium text-amber-700 dark:text-amber-300">Ambassador</span>
                    </div>
                    )
                    //  : (
                    // <div
                    //     className="w-fit flex items-center gap-2 px-3 py-1 -mt-3 md:mt-0 bg-gradient-to-r from-yellow-500/10 to-amber-500/10 border border-yellow-500/20 rounded-full cursor-pointer hover:bg-yellow-500/20 dark:hover:bg-yellow-500/20 transition-colors duration-600"
                    //     onClick={() => window.open('https://mocktrail.app/ambassador-program?utm_source=mocktrail-app&utm_medium=referral&utm_campaign=ambassador-program', '_blank')}
                    // >
                    //     <Crown size={15} className="text-yellow-600 dark:text-yellow-400" />
                    //     <span className="text-xs md:text-sm font-medium text-yellow-700 dark:text-yellow-300">Become an Ambassador</span>
                    // </div>
                    // )
                    }
                <h3 className="text-lg font-medium mb-3 flex items-center text-text-primary-light dark:text-text-primary-dark">
                    <Gift className="mr-2 text-primary-light dark:text-primary-dark" size={16} />
                    Invite Friends & Earn Benefits
                </h3>

          </div>
          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-4">
            Share your referral code with friends so that they signup using your code & prepare for their interviews. You get free minutes of Interview time.
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-text-primary-light dark:text-text-primary-dark">Share your referral link</label>
              <div className="form-field-container flex">
                <input
                  type="text"
                  value={referralLink || 'Loading...'}
                  readOnly
                  className="py-3 px-3 flex-grow font-medium font-heading text-base text-text-primary-light dark:text-text-primary-dark rounded-l-lg border border-border-light dark:border-border-dark"
                  disabled={isLoadingReferral}
                />
                <button
                  onClick={onCopyReferralLink}
                  className="px-4 py-3 text-base bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark hover:bg-primary-light/20 dark:hover:bg-primary-dark/20 transition-colors rounded-r-lg flex items-center border-t border-b border-r border-border-light dark:border-border-dark"
                  disabled={isLoadingReferral}
                >
                  {linkCopied ? <Check size={18} className='my-0.5' /> : <Copy size={18} className='my-0.5'/>}
                  {!isMobileView && <span className="ml-2 text-base font-medium">{linkCopied ? 'Copied!' : 'Copy'}</span>}
                </button>
              </div>
            </div>
            <div className="flex justify-between flex-col md:flex-row space-y-4 md:space-y-0">
              <div className="flex items-center space-x-3">
                <div className="bg-primary-light/10 dark:bg-primary-dark/10 p-2 rounded-full">
                  <Users size={18} className="text-primary-light dark:text-primary-dark" />
                </div>
                <div>
                  <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                    {referralStats.totalReferrals} users signed up
                  </p>
                  <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                    {referralStats.subscribed} subscribed
                  </p>
                </div>
              </div>
              <div className="flex gap-2 w-full md:w-auto">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={onOpenReferralStats}
                  className="font-medium w-full md:w-auto"
                  leftIcon={<ChartBar size={14} />}
                >
                  View Statistics
                </Button>
                {ambassadorStatus.isAmbassador && ambassadorStatus.status === 'approved' && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={onOpenAmbassadorPayments}
                    className="font-medium w-full md:w-auto"
                    leftIcon={<DollarSign size={14} />}
                  >
                    View Payments
                  </Button>
                )}
              </div>
            </div>
            <div className="mt-3 p-4 rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark">
              <h4 className="font-medium mb-2 text-text-primary-light dark:text-text-primary-dark">
                How it works
              </h4>
              <ul className="list-disc pl-5 space-y-1">
                <li className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                  Share your unique code with friends
                </li>
                <li className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                  When they <span className="font-medium text-text-primary-light dark:text-text-primary-dark">signup</span> using your code, you get <span className="font-medium text-text-primary-light dark:text-text-primary-dark">30 minutes</span> of interview time
                </li>
                <li className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                  When they <span className="font-medium text-text-primary-light dark:text-text-primary-dark">subscribe</span> to a plan, you get <span className="font-medium text-text-primary-light dark:text-text-primary-dark">60 minutes</span> of free interview time
                </li>
                {ambassadorStatus.isAmbassador && ambassadorStatus.status === 'approved' && (
                  <li className="text-sm text-amber-600 dark:text-amber-400 font-medium">
                    💰 As an ambassador, you earn 10% commission on the referrals who subscribe to a plan!
                  </li>
                ) 
                // : (
                //   <li className="text-sm text-yellow-600 dark:text-yellow-400 font-medium">
                //     Become an ambassador to earn 10% commission on the referrals who subscribe to a plan!
                //   </li>
                // )
                }
                <li className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                  Invite unlimited friends and earn more rewards
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ReferralModal; 